import { observer } from 'mobx-react'
import React from 'react'

import Box from '@mui/material/Box'
import PropTypes from 'prop-types'

/**
 * @description Office hours for a location, with consecutive days that share
 * the same hours collapsed into one row (e.g. "Mon–Thu 9:00 AM – 4:00 PM"),
 * and an optional "Open now" / "Closed now" badge based on the location's
 * timezone. Renders nothing when the location has no office hours.
 */
const OfficeHours = observer(({ location, showStatus = true, sx }) => {
  const summary = location?.officeHoursSummary
  if (!summary?.length) return null

  const openNow = showStatus ? location.isOpenNow() : null

  return (
    <Box
      className="office-hours"
      sx={[{ fontSize: 'inherit', lineHeight: 'inherit' }, sx]}
    >
      {openNow != null && (
        <Box
          component="span"
          role="status"
          sx={{
            display: 'inline-block',
            fontWeight: 600,
            color: openNow ? 'success.main' : 'error.main',
          }}
        >
          {openNow ? 'Open now' : 'Closed now'}
        </Box>
      )}
      <Box
        component="dl"
        aria-label="Office hours"
        sx={{
          display: 'grid',
          gridTemplateColumns: 'max-content 1fr',
          columnGap: 1.5,
          rowGap: 0,
          m: 0,
          '& dt': { fontWeight: 600, whiteSpace: 'nowrap' },
          '& dd': { m: 0, minWidth: 0, overflowWrap: 'anywhere' },
        }}
      >
        {summary.map(({ days, label }) => (
          <React.Fragment key={days}>
            <dt>{days}</dt>
            <dd>{label}</dd>
          </React.Fragment>
        ))}
      </Box>
    </Box>
  )
})

OfficeHours.displayName = 'OfficeHours'
OfficeHours.propTypes = {
  location: PropTypes.object,
  showStatus: PropTypes.bool,
  sx: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
}

export default OfficeHours
